import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";
import type { Plant } from "~/types/purchasing";

const RAW_MATERIAL_PLANT_KEY = "raw_material_stock_by_plant";
const OIL_TYPE_PLANT_KEY = "oil_type_stock_by_plant";

export type PerPlantStock = Record<Plant, number>;
export type StockByPlantMap = Map<string, PerPlantStock>;

const emptyPerPlant = (): PerPlantStock => ({ P1: 0, P2: 0, NC: 0 });

export function useRawMaterialStockByPlant() {
  return useQuery<StockByPlantMap, Error>({
    queryKey: [RAW_MATERIAL_PLANT_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("raw_material_stock_by_plant")
        .select("raw_material_id, plant, current_stock");
      if (error) throw error;
      const map: StockByPlantMap = new Map();
      for (const row of data ?? []) {
        const existing = map.get(row.raw_material_id) ?? emptyPerPlant();
        existing[row.plant as Plant] = Number(row.current_stock) || 0;
        map.set(row.raw_material_id, existing);
      }
      return map;
    },
  });
}

export function useOilTypeStockByPlant() {
  return useQuery<StockByPlantMap, Error>({
    queryKey: [OIL_TYPE_PLANT_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("oil_type_stock_by_plant")
        .select("oil_type_id, plant, current_stock_liters");
      if (error) throw error;
      const map: StockByPlantMap = new Map();
      for (const row of data ?? []) {
        const existing = map.get(row.oil_type_id) ?? emptyPerPlant();
        existing[row.plant as Plant] = Number(row.current_stock_liters) || 0;
        map.set(row.oil_type_id, existing);
      }
      return map;
    },
  });
}

export function getStockAt(
  map: StockByPlantMap | undefined,
  id: string,
  plant: Plant
): number {
  return map?.get(id)?.[plant] ?? 0;
}
